import { TranslateService } from '@ngx-translate/core';
import { Injectable, Injector } from '@angular/core';
import { map, tap, BehaviorSubject, Observable } from 'rxjs';
import { BaseApiService } from './baseapi.service';
import { Campaign, CampaignInfo, CampaignPageContent, CampaignTypeInfo, CampaignTypes, LiteCampaignTypes, MyCampaignDetails } from '../models/campaign.model';
import { CampaignInfoResponse, CampaignPageContentDetailsResponse, CampaignPageContentResponse, MyCampaignDetailsRepsonse } from '../models/api-models/campaign-api.model';
import { UtilsService } from './utils.service';
import { CampaignStateService } from './campaign-state.service';
import { DonationItem, DonationSummary } from '../models/donation.model';
import { KeyValueModel } from '../models/keyvalue.model';
import { HttpParams } from '@angular/common/http';
import { TransactionFeeSettings } from '../models/transaction-fee-settings.model';

@Injectable({
  providedIn: 'root',
})
export class CampaignService extends BaseApiService {

  public campaignData!: Campaign;
  public companyLogo$ = new BehaviorSubject<string>('');
  public companyLogo2$ = new BehaviorSubject<string>('');
  public companyLogoAlt$ = new BehaviorSubject<string>('');
  public companyLogo2Alt$ = new BehaviorSubject<string>('');
  public footerLinks$ = new BehaviorSubject<string>('');
  public footerCopyright$ = new BehaviorSubject<string>('');
  public currentCampaignGuid!: string;

  constructor(
    protected injector: Injector,
    private utilService: UtilsService,
    private campaignStateService: CampaignStateService,
    protected translateService: TranslateService
  ) {
    super(injector);
  }

   companyLogo = () => this.companyLogo$.asObservable();
   companyLogo2 = () => this.companyLogo2$.asObservable();
   companyLogoAlt = () => this.companyLogoAlt$.asObservable();
   companyLogo2Alt = () => this.companyLogo2Alt$.asObservable();
   footerLinks = () => this.footerLinks$.asObservable();
   footerCopyright = () => this.footerCopyright$.asObservable();

  getCampaingInfo(campaignGuid: string): Observable<Campaign>{
    const url = `${this.baseApiUrl}/Campaigns/${campaignGuid}`;
     return this.httpAuthGet(url).pipe(
       map(res=> this.mapCampaign(res.data)),
       tap( data=> {
         this.campaignData = data;
         this.currentCampaignGuid = campaignGuid;
       }));
  }

  getLatestClosedCampaignGuid(): Observable<string> {
    const url = `${this.baseApiUrl}/donor/closed-campaigns`;

    return this.httpAuthGet(url).pipe(
      map(resp => resp.data[0])
    );
  } 

  getDonorActiveCampaignList(): Observable<CampaignInfo[]>{
    const url = `${this.baseApiUrl}/donor/campaigns`;
    return this.httpAuthGet(url).pipe(
    // return this.httpAuthGet("assets/mock/campaignList.json").pipe(
       map(res=> res.data.map((cg: any) => this.mapCampaignInfoItem(cg))));
  }

  searchNationalLocation(searchText: string): Observable<KeyValueModel[]>{
    const url = `${this.baseApiUrl}/donor/search/city/${searchText}`; //TODO: Ruchira require endpoint on donor controller
    return this.httpAuthGet(url).pipe(
      map((res : {data:any[]})=> res.data.map((cg: any) => ({
         key: cg.suWay,
         value: `${cg.city}, ${cg.prov}`
       })
      )));
  }

  getDonationSummary(): Observable<DonationSummary[]>{
    const url = `${this.baseApiUrl}/donor/previousDonation`;
     return this.httpAuthGet(url).pipe(
     // return this.httpAuthGet("assets/mock/donationSummary.json").pipe(
       map(res=> res.data.map((cg: any) => this.mapDonationSummary(cg))));
  }

  getTaxReceipt(donationId: string | number): Observable<any>{
    const url = `${this.baseApiUrl}/donor/taxReceipt/${donationId}`;
    return this.httpAuthGetDownload(url);
  }

  getAprDonationSummary(): Observable<DonationSummary[]> {
    return this.getDonationSummary().pipe(
      map(list => {
        list = list.filter(d => d.isApr)
        return list;
      })
    )
  }

  isUserDonationExist(): Observable<boolean>{
    const campaignGuid = this.campaignStateService.getSelectedCampaign()?.campaignGuid;
    const url = `${this.baseApiUrl}/donor/previousDonation?campaignGuId=${campaignGuid}`;
     return this.httpAuthGet(url, this.getCampaignQueryParameter(campaignGuid)).pipe(
     // return this.httpAuthGet("assets/mock/donationSummary.json").pipe(
       map(res=> res.data && res.data.length > 0  ? true: false));
  }

  getCurrentCampaignGuid(){
    return this.currentCampaignGuid ? this.currentCampaignGuid : localStorage.getItem('guid');
  }

  getPageContent(campaignGuid: string, pageType: string): Observable<Map<string, CampaignPageContent>>{
    const cGuid =  campaignGuid; //localStorage.getItem('guid');

    let workplaceId;       

    if (this.campaignStateService.getKioskId()) {
      workplaceId = this.campaignStateService.getKioskId();        
    }

    if (this.campaignStateService.getUWId()) {
      workplaceId = this.campaignStateService.getUWId();
    }

    workplaceId = workplaceId || '';

    const url = `${this.baseApiUrl}/content/campaign/${cGuid}/page/${pageType}?workplaceId=${workplaceId}`;

    return this.httpAuthGet(url).pipe(
      map(res=> res && res.data ? res.data.map((item:any) => this.mapPageContent(item)) : []
      ), map(pageArray=> this.getPageContentDictionary(pageArray)) );
  }

  getDonatePageContent(campaignGuid: string, donationTotal: number | string, _pageType: string='Donate'): Observable<Map<string, CampaignPageContent>>{
    const cGuid =  campaignGuid; //localStorage.getItem('guid');
    const url = `${this.baseApiUrl}/donor/page/Donate/amount/${donationTotal}/campaign/${cGuid}`;
     return this.httpAuthGet(url).pipe(
       map(res=> res && res.data ? res.data.map((item:any) => this.mapPageContent(item)) : []
       ), map(pageArray=> this.getPageContentDictionary(pageArray)) );
  }

  getCampaignKioskProvinceList(campaignGuid: string): Observable<KeyValueModel[]>{
    const url = `${this.baseApiUrl}/donor/workplaceProvinces/${campaignGuid}`;
     return this.httpAuthGet(url).pipe(
       map((res : {data:any[]})=> res.data.map((cg: any) => ({
          key: cg.provinceCode ? cg.provinceCode.trim() :  cg.provinceCode,
          value: cg.provinceCode ? cg.provinceCode.trim() :  cg.provinceCode
        })
       )));
  }

  getCampaignKioskList(campaignGuid: string, provinceCode: string): Observable<KeyValueModel[]>{
    //let encodedProvinceCode = provinceCode.replace(/&/g,'%26');
    const encodedProvincecode= encodeURIComponent(provinceCode);
    const url = `${this.baseApiUrl}/donor/workplaceProvinces/${campaignGuid}/province?provinceCode=${encodedProvincecode}`;
     return this.httpAuthGet(url).pipe(
       map(res=> res.data.map((cg: any) => ({
          key: cg.campaignWorkplaceID,
          value: cg.name
        })
       )));
  }

  getMyCampaignDetails(campaignGuid: string): Observable<MyCampaignDetails> {
    const url = `${this.baseApiUrl}/donor/campaign/${campaignGuid}/thermometer`;
     return this.httpAuthGet(url).pipe(
      map(res=> this.mapMyCampaignDetails(res.data))
     );
  }

  mapMyCampaignDetails(res: MyCampaignDetailsRepsonse): MyCampaignDetails {
    return {
      isThermometerVisible:  res.isThermometerVisible,
      isGroupThermometerVisible:  res.isGroupThermometerVisible,
      isParticipationThermometerVisible:  res.thermometer.isParticipationThermometerVisible,
      isPledgeThermometerVisible:  res.thermometer.isPledgeThermometerVisible,
      isUWThermometerVisible:  res.thermometer.isUWThermometerVisible,
      displayPledgeAmount: res.thermometer.showDollar,
      displayUwPledgeAmount: res.thermometer.showDollar,
      pledgeAmount: res.thermometer.campaignCurrentDollar,
      pledgePercent: `${res.thermometer.campaignCurrentPercentage}`,
      uwPledgeAmount: res.thermometer.uwCampaignCurrentDollar,
      curCampaignAmountGoal: res.thermometer.curCampaignAmountGoal,
      uwAmountGoal: res.thermometer.uwAmountGoal,
      uwPledgePercent: `${res.thermometer.uwCampaignCurrentPercentage}`,
      participationPercentage: `${res.thermometer.campaignPercentage}`,
      groupDetails: res.groupParticipation.map((group) => {
        return {
          city: group.groupName,
          percentage: `${group.participationPercentage}`
        }
      })
    }
  }

  mapCampaign(res: CampaignPageContentDetailsResponse): Campaign {
      return {
        info : this.mapCampaignInfo(res.info, res.profile, res.campaignFeesDetail),
        type: this.mapCampaignType(res.type),
        campaignFeesDetail: res.campaignFeesDetail
      }
  }

  mapCampaignInfoItem(res: any): CampaignInfo {
    return {
      ...res,
      homeEmailDisplayed: res?.isHomeEmailDisplayed,
      homeEmailRequired: res?.isHomeEmailEditable,
      workEmailDisplayed: res?.isWorkEmailDisplayed,
      workEmailRequired: res?.isWorkEmailEditable,
      recognitionThresholdAmount: res?.recognitionThresholdAmount,
      isBilingual: this.isProfileBilingual(res?.profile?.languages),
      campaignType: this.getLiteCampaignType(res) ? CampaignTypes.Lite : CampaignTypes.Regular,
      languageSelectionEnabled: res.showLandingPage
    }
  }

  isProfileBilingual(language: string[]){
      return language && language.length > 1 && language.join(',').includes('fr');
  }

  mapDonationSummary(res: any): DonationSummary {
    return {
      donationId: res.donationId,
      paymentMethod: res.paymentMethodName,
      paymentMethod_fr: res.paymentMethodName_fr,
      donationDate: res.donationDate,
      billingDate: res.billingDate,
      totalDonation: res.curDonationAmount,
      campaignName: res.campaignName,
      campaignGuid: res.campaignGuid,
      isDeleted: res.deletedDateTime == null ? false : true,
      donationSourceCode: res.donationSourceCode,
      isApr: res.isAprEditAllow,
      isEditAllow: res.isEditAllow,
      isPledge: res.isPledge,
      isETaxExists: res.isETaxExists,
      transactionFees: res.transactionFees,
      parentDonationId: res.parentDonationId,
      designations: res.designations,
      donationItems:  res.designations.map((dest:any)=> this.mapDonationSummaryCharityItems(dest))
    }
  }

  mapDonationSummaryCharityItems(res: any): DonationItem {
    return {
      charityName: res.name,
      amount: res.amount
    }
  }

  mapCampaignInfo(res: CampaignInfoResponse, profile?: any, campaignFeesDetail?: TransactionFeeSettings): CampaignInfo {
    return {
      campaignId: res.iCampaignID,
      campaignGuid: res.campaignGuid,
      campaignName: res.campaignName,
      companyId: res.iCompanyID,
      profileId: res.iProfileID,
      customFieldOnSummaryEnabled: res.customFieldOnSummaryEnabled,
      profileName: res.profileName,
      unitedWayID: res.iUnitedWayID,
      unitedWayShortName: res.unitedWayShortName,
      unitedWayLanguageCode: res.unitedWayLanguageCode,
      countryCode: res.countryCode,
      liteCampaignType : this.getLiteCampaignType(res),
      campaignType: this.getLiteCampaignType(res) ? CampaignTypes.Lite : CampaignTypes.Regular,
      isPayPeriodFlexible: res.isPayPeriodFlexible,
      maximumFrequency: res.maximumFrequency,
      homeEmailDisplayed: res.isHomeEmailDisplayed,
      homeEmailRequired: res.isHomeEmailEditable,
      workEmailDisplayed: res.isWorkEmailDisplayed,
      workEmailRequired: res.isWorkEmailEditable,
      recognitionThresholdAmount: res?.recognitionThresholdAmount,
      provinceLabel: res.provinceLabel,
      provinceLabel_fr: res.provinceLabel_fr,
      kioskLabel: res.kioskLabel,
      kioskLabel_fr: res.kioskLabel_fr,
      isBilingual: this.isProfileBilingual(profile?.languages),
      profile: profile,
      isEventField1Required: res.isEventField1Required,
      isEventField2Required: res.isEventField2Required,
      isEventField1Visible: res.isEventField1Visible,
      isEventField2Visible: res.isEventField2Visible,
      eventField1: res.eventField1,
      eventField2: res.eventField2,
      enDonorPromptMsg: res.enDonorPromptMsg,
      frDonorPromptMsg: res.frDonorPromptMsg,
      isAprPromptEnabled: res.isAprPromptEnabled,
      isPrivacyPolicyVisible: res.isPrivacyPolicyVisible,
      isEmailSubscriptionEnabled: res.isEmailSubscriptionEnabled,
      enTermAndConditionLabel: res.enTermAndConditionLabel,
      frTermAndConditionLabel: res.frTermAndConditionLabel,
      enTermAndConditionContent: res.enTermAndConditionContent,
      frTermAndConditionContent: res.frTermAndConditionContent,
      languageSelectionEnabled: res.showLandingPage,
      campaignFeesDetail: res.campaignFeesDetail || campaignFeesDetail
    }
   }

   mapCampaignType(res: CampaignTypeInfo): CampaignTypeInfo {
    return {
      name: res.name,
      isLiteCampaign: res.isLiteCampaign,
      type: res.type,
      isEvent: res.isEvent,
      isCampaignClosed: res.isCampaignClosed
    }
   }

   mapPageContent(res: CampaignPageContentResponse): CampaignPageContent {
    return {
      id: res?.id,
      windowID:  res?.windowID,
      name:  res?.name,
      description:  res?.description,
      contentXML:  res?.contentXML,
      filterHeaderId:  res?.filterHeaderID,
      campaignId:  res?.campaignID,
    }
  }


  getPageContentDictionary(pageContent: CampaignPageContent[]){
    const pageContentMap = new Map();

    pageContent.forEach(data=>{

      if( data.name === 'Logo_1') {
        this.companyLogo$.next(this.utilService.getImageSrcName(data.contentXML) ?? '');
        this.companyLogoAlt$.next(this.utilService.getImageAltText(data.contentXML) ?? '');
      }

      if( data.name === 'Logo_2') {
        this.companyLogo2$.next(this.utilService.getImageSrcName(data.contentXML) ?? '');
        this.companyLogo2Alt$.next(this.utilService.getImageAltText(data.contentXML) ?? '');

      }

      if( data.name === 'Footer') {
        this.footerLinks$.next(data.contentXML);
      }

      if( data.name === 'Copyright') {
        this.footerCopyright$.next(data.contentXML);
      }

      if (!pageContentMap.has(data.name)){
        pageContentMap.set(data.name, data);
      }
    });
    return pageContentMap;
  }

  getLiteCampaignType(res: any): any {
    if (!res)
      return null;
    if (res.bLiteCampaign || res.liteCampaignType == LiteCampaignTypes.Lite)
      return LiteCampaignTypes.Lite;
    if (res.bLiteKiosk || res.liteCampaignType == LiteCampaignTypes.Kiosk)
      return LiteCampaignTypes.Kiosk;
    if (res.bLiteNational || res.liteCampaignType == LiteCampaignTypes.National)
      return LiteCampaignTypes.National
  }

  private getCampaignQueryParameter(campignGuid: string) {
    return new HttpParams()
      .set('campaignGuId', campignGuid)
  }

}
